import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Convert a Twilio recording URL to use our backend proxy endpoint
 * This avoids requiring Twilio authentication on the client side
 * Uses relative URL so it goes through the frontend domain (app.insuracentral.com)
 * and gets proxied to the backend via Next.js rewrites
 *
 * Input: https://api.twilio.com/2010-04-01/Accounts/{AccountSid}/Recordings/{RecordingSid}.mp3
 * Output: /api/communication/recordings/{RecordingSid}
 * Telnyx recordings are UUIDs (rows store {BACKEND_URL}/api/communication/recordings/{uuid});
 * the proxy fetches a fresh media URL for them, since Telnyx's expire after 10 minutes.
 */
export function getProxyRecordingUrl(recordingUrl: string | null | undefined): string | null {
  if (!recordingUrl) return null;

  // Extract the recording SID (RE followed by 32 hex chars) from any URL format:
  // 1. Raw Twilio URL: https://api.twilio.com/.../Recordings/RE598757426baae6b8b070425c75ed6410
  // 2. Already-proxied URL: https://backend.com/api/communication/recordings/RE598757426baae6b8b070425c75ed6410
  // 3. Wrongly-proxied URL (localhost): http://localhost:8080/api/communication/recordings/RE...
  // or a Telnyx recording id: bare, or right after /recordings/ (never some other UUID in the URL)
  const match =
    recordingUrl.match(/(RE[a-f0-9]{32})/i) ||
    recordingUrl.match(/(?:^|\/recordings\/)([0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12})(?![0-9a-f-])/i);
  if (!match) {
    // No recording SID found — return as-is (could be a direct audio file URL)
    return recordingUrl;
  }

  const recordingSid = match[1];
  return `/api/communication/recordings/${recordingSid}`;
}

/**
 * Fetch a call recording with the JWT and return a Blob object URL the
 * `<audio>` element can play.
 *
 * Why: <audio src=proxyUrl> can't send the Authorization header, so the
 * backend's JWT guard 401s every playback request. Confirmed in Render
 * logs — every recording fetch from May 30 (and before) returned 401, even
 * though the UI showed a play button. Result: agents saw "0:00" duration
 * and silent playback for months.
 *
 * Trade-off vs. native HTML5 streaming: we lose Range / progressive
 * playback — the entire MP3 has to download before any audio plays. For
 * typical recordings (1–20 MB) the wait is 1–5s on normal Wi-Fi.
 * Acceptable; matches the security model of the download flow.
 *
 * The returned object URL MUST be revoked by the caller (typically in a
 * useEffect cleanup) — otherwise it pins the Blob in memory until the
 * page unloads.
 */
export async function loadRecordingAsObjectUrl(
  proxyUrl: string,
): Promise<string> {
  // SECURITY: never attach the Bearer JWT to a URL that doesn't resolve to
  // the current origin. The callers fall back to `recordingUrl` (raw Twilio
  // or potentially DB-poisoned URL) when getProxyRecordingUrl can't build a
  // proxy path — without this guard, a corrupt recording_url in the DB could
  // exfiltrate the agent's JWT to an attacker-controlled host.
  const resolved = new URL(proxyUrl, window.location.origin);
  if (resolved.origin !== window.location.origin) {
    throw new Error("Recording URL must be same-origin");
  }

  const { getSupabaseSession } = await import("@/app/_module/services/auth");
  const session = await getSupabaseSession();
  const token = session?.access_token;
  if (!token) {
    throw new Error("Not signed in — please refresh and try again.");
  }

  const response = await fetch(resolved.toString(), {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error(`Recording fetch failed (${response.status})`);
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

/**
 * Download a call recording to disk via our backend proxy.
 *
 * Why this exists: the obvious `<a download href=proxyUrl>` click triggers a
 * top-level browser navigation, which doesn't carry the `Authorization: Bearer`
 * header our backend's JWT guard requires. Result: the proxy 401s, Chrome's
 * download manager shows "Try to sign in to the site," and the file never
 * lands on disk. Reported by Mark Dusevic 2026-05-30 (multiple agents hit it).
 *
 * Fix: fetch the file with the Authorization header set, get the bytes as a
 * Blob, then trigger the save via URL.createObjectURL — same UX (click → file
 * lands in Downloads), works because the JWT is now on the request.
 */
export async function downloadRecording(
  proxyUrl: string,
  fileName: string,
): Promise<void> {
  // SECURITY: same-origin check before attaching the JWT — see
  // loadRecordingAsObjectUrl for the full rationale. Callers fall back to
  // the raw recordingUrl when getProxyRecordingUrl can't proxy it, which
  // would otherwise leak the Bearer token to whatever host the DB had.
  const resolved = new URL(proxyUrl, window.location.origin);
  if (resolved.origin !== window.location.origin) {
    throw new Error("Recording URL must be same-origin");
  }

  // Lazy import to avoid pulling auth (and supabase) into modules that only
  // need cn()/getProxyRecordingUrl(). The import is resolved once at runtime.
  const { getSupabaseSession } = await import("@/app/_module/services/auth");
  const session = await getSupabaseSession();
  const token = session?.access_token;
  if (!token) {
    throw new Error("Not signed in — please refresh and try again.");
  }

  const response = await fetch(resolved.toString(), {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error(`Download failed (${response.status})`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    // Defer revoke so the browser has time to start the download.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }
}
